/**
 * The landlord security rows: a table on desktop, cards below 768px.
 *
 * THE VERMIETER COLUMN, AND WHAT IT STILL CANNOT SHOW
 * ---------------------------------------------------
 * The `/deposits/landlord-placed` contract gained `landlord_id` and
 * `landlord_name` in K4a, so the Verwaltung behind a security is now a fact the
 * endpoint answers rather than something a column would have had to guess. The
 * name is the backend's — company first, contact person second — and it is never
 * assembled here.
 *
 * A unit with no `landlord_id` renders "—" and no link. That is not a defect to
 * be papered over with the owner, the property manager or the property's name:
 * those are different people with different roles, and putting one of them under
 * "Vermieter" would misattribute a security deposit. The em dash says what is
 * true — this unit has no Verwaltung on file.
 *
 * THE PROVIDER IS A SUBLINE, NOT A NINTH COLUMN
 * ----------------------------------------------
 * K4b.1 added `provider` to the contract, and it renders under the security
 * type rather than in a column of its own. Measured in Chromium against the
 * production build, a ninth column costs 39px at 1280 and 55px at 1600 — two
 * widths that are clean today — and turns the accepted 23px at 1536 into 119px,
 * far enough to swallow the Hinweis column. The subline costs zero at every
 * width; it only makes an insured row one line taller. A per-row status is still
 * absent because it is still absent from the payload.
 *
 * The one hint below is derived, and it mirrors a backend counter exactly rather
 * than inventing a rule of its own.
 *
 * Layout switching follows the tenant table: one copy in the DOM via
 * `useIsMobile`, not two via `hidden md:block`.
 */
import React from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import { chf, chfPrecise } from "../../../components/admin/companyFinance/financeFormat";
import { useIsMobile } from "../../../hooks/useIsMobile";
import DepositBadge from "./DepositBadge";
import { landlordPremiumMissing } from "./depositsFilters";
import { depositTypeLabel, providerLabel } from "./depositsLabels";

const HEAD =
  "px-2.5 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground";
const CELL = "px-2.5 py-2.5 align-top text-[12.5px] text-foreground";

function unitLabel(item) {
  return String(item.short_unit_id || item.title || "").trim() || "—";
}

function locationLabel(item) {
  return [item.address, item.city].map((v) => String(v || "").trim()).filter(Boolean);
}

/**
 * The Verwaltung cell. A link only when the row carries BOTH an id to point at
 * and a name to show: a link labelled "—" would be a trap, and a name without an
 * id has nowhere to go.
 */
function LandlordCell({ item, className }) {
  const name = String(item.landlord_name || "").trim();
  if (!name) {
    return <span className={className}>—</span>;
  }
  if (!item.landlord_id) {
    return <span className={className}>{name}</span>;
  }
  return (
    <Link
      to={`/admin/landlords/${item.landlord_id}`}
      aria-label={`Vermieter ${name} öffnen`}
      className={`rounded-sm underline-offset-2 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className || ""}`}
    >
      {name}
    </Link>
  );
}

/**
 * The security type, with the issuer beneath it when there is one to name.
 *
 * Only an insured security has an issuer, so bank, cash and none render the type
 * alone — no "Anbieter · —" placeholder for something that does not exist in the
 * first place. An insured security WITHOUT a recorded issuer is different: that
 * is a gap someone can close, so it says so. `providerLabel` already draws that
 * distinction for the tenant table and is reused verbatim.
 */
function SecurityTypeCell({ item }) {
  const type = String(item.deposit_type || "").trim().toLowerCase();
  return (
    <>
      {depositTypeLabel(item.deposit_type)}
      {type === "insurance" ? (
        <span
          data-testid={`deposits-landlord-provider-${item.unit_id}`}
          className="mt-0.5 block text-[11px] text-muted-foreground"
        >
          {`Anbieter · ${providerLabel(item.provider, item.deposit_type)}`}
        </span>
      ) : null}
    </>
  );
}

/** The only client-derived signal on this side, mirroring the backend's
 *  `insurance_units_without_annual_premium` condition one to one. */
function PremiumHint({ item }) {
  if (!landlordPremiumMissing(item)) return null;
  return (
    <DepositBadge tone="warn" testId={`deposits-landlord-gap-${item.unit_id}`}>
      Prämie fehlt
    </DepositBadge>
  );
}

/**
 * K4c.2: this was a `<Link>` to /admin/units/{id}. It now opens the security
 * detail modal, and the same `<Link>` lives inside it as "Einheit öffnen" —
 * so the row keeps one action and the navigation keeps working. See the
 * tenant table's DetailAction for the full reasoning.
 */
function DetailAction({ item, onOpenDetails, className }) {
  if (!onOpenDetails) return null;
  return (
    <button
      type="button"
      onClick={() => onOpenDetails(item)}
      aria-label={`Sicherheitsleistung für ${unitLabel(item)} anzeigen`}
      data-testid={`deposits-landlord-details-${item.unit_id}`}
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className || ""}`}
    >
      <span className="sr-only md:not-sr-only">Details</span>
      <ChevronRight aria-hidden className="h-3.5 w-3.5" />
    </button>
  );
}

function DesktopTable({ items, onOpenDetails }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <caption className="sr-only">Sicherheitsleistungen bei Vermietern</caption>
        <thead>
          <tr className="border-b border-border">
            <th scope="col" className={HEAD}>Einheit</th>
            <th scope="col" className={HEAD}>Vermieter</th>
            <th scope="col" className={HEAD}>Standort</th>
            <th scope="col" className={HEAD}>Sicherheitsart</th>
            <th scope="col" className={`${HEAD} text-right`}>Betrag</th>
            <th scope="col" className={`${HEAD} text-right`}>Jahresprämie</th>
            <th scope="col" className={HEAD}>Hinweis</th>
            <th scope="col" className={`${HEAD} text-right`}>
              <span className="sr-only">Aktion</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const loc = locationLabel(item);
            return (
              <tr
                key={item.unit_id}
                data-testid={`deposits-landlord-row-${item.unit_id}`}
                className="border-b border-border/60 last:border-0 hover:bg-muted/40"
              >
                <td className={`${CELL} font-medium`}>
                  {unitLabel(item)}
                  {item.title && item.short_unit_id ? (
                    <span className="mt-0.5 block text-[11px] font-normal text-muted-foreground">
                      {item.title}
                    </span>
                  ) : null}
                </td>
                <td
                  className={`${CELL} text-muted-foreground`}
                  data-testid={`deposits-landlord-name-${item.unit_id}`}
                >
                  <LandlordCell item={item} />
                </td>
                <td className={`${CELL} text-muted-foreground`}>
                  {loc.length ? loc[0] : "—"}
                  {loc.length > 1 ? (
                    <span className="mt-0.5 block text-[11px]">{loc[1]}</span>
                  ) : null}
                </td>
                <td className={CELL}>
                  <SecurityTypeCell item={item} />
                </td>
                <td className={`${CELL} text-right tabular-nums`}>
                  {chf(item.deposit_amount_chf)}
                </td>
                <td className={`${CELL} text-right tabular-nums`}>
                  {/* Rappen: the cost breakdown divides this by twelve. */}
                  {chfPrecise(item.annual_premium_chf)}
                </td>
                <td className={CELL}>
                  <PremiumHint item={item} />
                </td>
                <td className={`${CELL} text-right`}>
                  <DetailAction item={item} onOpenDetails={onOpenDetails} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MobileCards({ items, onOpenDetails }) {
  return (
    <ul className="m-0 list-none space-y-2 p-0">
      {items.map((item) => {
        const loc = locationLabel(item);
        return (
          <li
            key={item.unit_id}
            data-testid={`deposits-landlord-row-${item.unit_id}`}
            className="rounded-lg border border-border p-3"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="m-0 text-[13px] font-medium text-foreground">
                  {unitLabel(item)}
                </p>
                <p className="m-0 text-[11px] text-muted-foreground">
                  {loc.length ? loc.join(", ") : "—"}
                </p>
              </div>
              <PremiumHint item={item} />
            </div>

            {/* Labelled, unlike the unit and the location: a bare company name in
                a card would read as a second address line. */}
            <p
              className="m-0 mt-2 text-[11px] text-muted-foreground"
              data-testid={`deposits-landlord-name-${item.unit_id}`}
            >
              <span className="font-medium text-foreground">Vermieter: </span>
              <LandlordCell item={item} />
            </p>

            <div className="mt-2 flex items-end justify-between gap-2">
              <p className="m-0 text-[12px] text-foreground">
                <SecurityTypeCell item={item} />
                <span className="block text-[11px] text-muted-foreground">
                  {`Prämie p.a.: ${chfPrecise(item.annual_premium_chf)}`}
                </span>
              </p>
              <span className="shrink-0 text-[13px] font-semibold tabular-nums text-foreground">
                {chf(item.deposit_amount_chf)}
              </span>
            </div>

            <div className="mt-2 flex justify-end">
              <DetailAction item={item} onOpenDetails={onOpenDetails} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default function LandlordDepositsTable({ items, onOpenDetails }) {
  const isMobile = useIsMobile();
  if (!items.length) return null;
  return isMobile ? (
    <MobileCards items={items} onOpenDetails={onOpenDetails} />
  ) : (
    <DesktopTable items={items} onOpenDetails={onOpenDetails} />
  );
}
